import { ISectionLogger } from "@multiweb/logging";
import { ServiceWorkerConnector } from "OW.Remoting/Basic/ServiceWorkerConnector";

/**
 * Zaregistruje Service Worker a napojí se na něj. Pomocí callbacků lze reagovat na různé stavy registrace.
 * @param onActivated Callback volaný při každém napojení na běžící SW
 * @param onUpdateFound Callback spuštěný v okamžiku nalezení updatu SW
 * @param onUpdateReady Callback spuštěný v okamžiku připraveného updatu SW
 * @param onError Callback volaný při chybě registrace SW
 * @param serviceWorkerLogger Logger, přes který bude tento worker logovat
 */
export const bootstrapServiceWorker = async (
    onActivated: (connector: typeof ServiceWorkerConnector) => void,
    onUpdateFound: () => Promise<void>,
    onUpdateReady: () => Promise<void>,
    onError: (error: any) => void,
    serviceWorkerLogger: ISectionLogger,
) => {
    let refreshing = false;
    let runningUpdateGui: Promise<void> | null = null;

    navigator.serviceWorker.addEventListener("controllerchange", () => {
        serviceWorkerLogger.log("Controller changed", runningUpdateGui);

        if (refreshing) // kvůli zacyklení při použití _Update on reload_ v DevTools
            return;

        refreshing = true;

        if (!runningUpdateGui) {
            serviceWorkerLogger.log("Controller changed - stopped");
            return;
        }

        serviceWorkerLogger.log("Controller changed - refreshing page");
        runningUpdateGui.then(() => window.location.reload()); // refresh se provede až doběhnutí zobrazeného GUI (Firefox)
    });

    if (!navigator.serviceWorker.controller)
        onUpdateFound();

    try {
        const registration = await navigator.serviceWorker.register("js/serviceworker/TescoSW.ServiceWorker.Loader.js", { scope: "./" });

        if (!registration) {
            serviceWorkerLogger.error("Unable to retrieve registration of existing worker");
            return;
        }

        const onUpdateFinished = () => {
            if (!runningUpdateGui) {
                runningUpdateGui = onUpdateReady()
                    .then(() => triggerUpdateActivationAndReload(registration));
            }
        };

        if (registration.waiting) {
            onUpdateFinished();
        } else {
            const waitForStateChange = (worker: ServiceWorker | null) => {
                serviceWorkerLogger.log("Found new update");
                onUpdateFound();

                worker?.addEventListener("statechange", () => {
                    serviceWorkerLogger.log("State changed: ", worker.state);

                    if (worker.state === "installed")
                        onUpdateFinished();
                });
            };

            if (registration.installing)
                waitForStateChange(registration.installing);

            registration.addEventListener("updatefound", () => waitForStateChange(registration.installing));
        }

        if (navigator.serviceWorker.controller) {
            ServiceWorkerConnector.init(serviceWorkerLogger);
            onActivated(ServiceWorkerConnector);
        }
    } catch (e) {
        onError(e);
    }
};

const triggerUpdateActivationAndReload = (registration: ServiceWorkerRegistration) => {
    if (registration.waiting)
        registration.waiting.postMessage("update");
};
